export const CHAT_HISTORY_LIMIT = 80;

export const truncateFileName = (name, max = 32) => {
  if (!name) return '';
  if (name.length <= max) return name;
  return `${name.substring(0, 14)}...${name.substring(name.length - 12)}`;
};

export const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

export const getFileExtension = (name = '') => {
  const parts = name.split('.');
  return parts.length > 1 ? parts.pop().toLowerCase() : '';
};

export const getFileMeta = (name = '') => {
  const ext = getFileExtension(name);
  const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'];
  const docExts = ['doc', 'docx', 'txt', 'rtf'];
  const sheetExts = ['xls', 'xlsx', 'csv'];

  if (imageExts.includes(ext)) {
    return { type: 'image', color: 'text-pink-600', bg: 'bg-pink-100 dark:bg-pink-900/30', label: 'IMG' };
  }
  if (ext === 'pdf') {
    return { type: 'pdf', color: 'text-red-600', bg: 'bg-red-100 dark:bg-red-900/30', label: 'PDF' };
  }
  if (docExts.includes(ext)) {
    return { type: 'doc', color: 'text-blue-600', bg: 'bg-blue-100 dark:bg-blue-900/30', label: 'DOC' };
  }
  if (sheetExts.includes(ext)) {
    return { type: 'sheet', color: 'text-green-600', bg: 'bg-green-100 dark:bg-green-900/30', label: 'XLS' };
  }
  return { type: 'file', color: 'text-gray-600', bg: 'bg-gray-100 dark:bg-gray-800', label: ext.toUpperCase() || 'FILE' };
};

export const normalizeMessageFiles = (msg) => {
  if (msg?.files?.length) {
    return msg.files.map((file) => ({
      fileUrl: file.fileUrl,
      originalName: file.originalName || file.name,
      size: file.size,
      mimeType: file.mimeType,
    }));
  }
  if (msg?.fileUrl) {
    return [{
      fileUrl: msg.fileUrl,
      originalName: msg.originalName,
      size: msg.size,
      mimeType: msg.mimeType,
    }];
  }
  return [];
};

export const buildFileUrl = (baseUrl, fileUrl) => {
  if (!fileUrl) return '#';
  if (fileUrl.startsWith('http')) return fileUrl;
  if (fileUrl.startsWith('/')) return `${baseUrl}${fileUrl}`;
  return `${baseUrl}/uploads/chat/${fileUrl.split('/').pop()}`;
};

export const emitChatTyping = (socket, { role, userId, targetUserId, isTyping }) => {
  if (!socket) return;
  if (role === 'user') {
    socket.emit('typing', { userId, isTyping });
    return;
  }
  socket.emit('typing', { targetUserId, senderRole: role, isTyping });
};

export const emitFileUploading = (socket, { role, userId, isUploading }) => {
  if (!socket || !userId) return;
  socket.emit('fileUploading', { userId, isUploading, senderRole: role });
};

export const appendSelectedFiles = (prev, incoming, maxFiles = 10, maxSize = 200 * 1024 * 1024) => {
  if (prev.length + incoming.length > maxFiles) {
    return { files: prev, error: `Maximum ${maxFiles} files allowed.` };
  }
  for (const file of incoming) {
    if (file.size > maxSize) {
      return { files: prev, error: `${file.name} exceeds 200MB limit.` };
    }
  }
  return { files: [...prev, ...incoming], error: null };
};

export const parseHistoryResponse = (data) => {
  if (Array.isArray(data)) {
    return { messages: data, hasMore: false };
  }
  return {
    messages: data?.messages || [],
    hasMore: Boolean(data?.hasMore),
  };
};

export const mergeUniqueMessages = (existing, incoming) => {
  const map = new Map(existing.map((message) => [message._id, message]));
  incoming.forEach((message) => map.set(message._id, message));
  return Array.from(map.values()).sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );
};

export const getSupportAgentName = (msg) => {
  if (msg?.sender !== 'admin') return null;
  const name = (msg?.user || '').trim();
  if (!name || name === 'Admin' || name === 'BooksComply Support') return null;
  return name;
};

export const formatSupportHeaderTitle = (employee) => {
  if (employee?.name) return `${employee.name} (BooksComply Support)`;
  return 'BooksComply Support';
};
